// --- React & Redux
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import { Drawer } from 'antd';
import Button from '@dtci/spellbook/Button';
// --- Styles
import './styles.scss';
// --- Hooks & Actions
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useDateMathActions } from 'datastage/store/dateMath/useDateMath';
import { useToasts } from 'datastage/utils/hooks/useToasts';
// --- Components
import { MacrosForm } from './macros-form';
// --- Utilities & API
import { datastageApi } from 'datastage/api';
import { macros } from 'datastage/utils/macros';

// Helper to check if a value is a meaningful number (not empty, not zero)
const hasValue = (val) => val !== '' && val !== null && val !== undefined && Number(val) !== 0;

// Validate if the "Create Macro" button should be enabled
function isCreateEnabled({ dealSelected, startDateTriggers, endDateTriggers }) {
    if (!dealSelected) return false;

    const hasStartTimeValue =
        hasValue(startDateTriggers.years) ||
        hasValue(startDateTriggers.months) ||
        hasValue(startDateTriggers.weeks) ||
        hasValue(startDateTriggers.days);

    const hasEndTimeValue =
        hasValue(endDateTriggers.years) ||
        hasValue(endDateTriggers.months) ||
        hasValue(endDateTriggers.weeks) ||
        hasValue(endDateTriggers.days);

    const isStartValid =
        startDateTriggers.useExistingStartDate ||
        (hasStartTimeValue && (!hasValue(startDateTriggers.days) || !!startDateTriggers.dayType));

    const isEndValid =
        endDateTriggers.useExistingEndDate ||
        (hasEndTimeValue && (!hasValue(endDateTriggers.days) || !!endDateTriggers.dayType));

    return isStartValid && isEndValid;
}

/**
 * Macros Drawer
 *
 * Features:
 * - Renders a drawer for creating Date Math Macros
 * - Handles macro creation and triggers refresh via refreshDateMath flag
 *
 * @returns {JSX.Element}
 */
export const MacrosDrawer = () => {
    const {
        isMacrosOpen,
        dealSelected,
        startDateTriggers,
        endDateTriggers,
        selectedMacroType,
    } = useSelector((state) => state.dateMath);
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { showErrorToast, showSuccessToast } = useToasts();
    const { updateDateMath, clearDateMathMacros } = useDateMathActions();

    // Handle closing the drawer (no refresh needed for cancel)
    const handleClose = useCallback(() => {
        clearDateMathMacros();
        updateDateMath({ isMacrosOpen: false });
    }, [updateDateMath, clearDateMathMacros]);

    // Handle macro creation with refresh via flag pattern
    const handleCreateMacro = async () => {
        setLoadingTrue();
        try {
            const res = await datastageApi.createMacro(
                macros(dealSelected, startDateTriggers, endDateTriggers, selectedMacroType),
            );
            if (res?.code) {
                showErrorToast(res?.message || 'Failed to create macro.');
                return;
            }

            // Close drawer and trigger refresh via the refreshDateMath flag
            clearDateMathMacros();
            updateDateMath({
                isMacrosOpen: false,
                refreshDateMath: true,
            });
            showSuccessToast('Macro created successfully.');
        } catch (error) {
            showErrorToast(error?.message || 'Failed to create macro.');
            console.error('Error creating macro:', error);
        } finally {
            setLoadingFalse();
        }
    };

    return (
        <Drawer
            className="macros-drawer"
            title="Date Math Macros"
            width={1025}
            placement="right"
            open={isMacrosOpen}
            onClose={handleClose}
            destroyOnClose
            bodyStyle={{ paddingBottom: 80 }}
            footer={
                <div className="button-row">
                    <Button
                        iconType="fa-solid fa-xmark"
                        labelText="close"
                        styleType="link"
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                    <Button
                        iconType="fa-solid fa-wrench"
                        labelText="create"
                        styleType="link"
                        onClick={handleCreateMacro}
                        disabled={
                            !isCreateEnabled({ dealSelected, startDateTriggers, endDateTriggers })
                        }
                    >
                        Create Macro
                    </Button>
                </div>
            }
        >
            <MacrosForm />
        </Drawer>
    );
};
